if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('mini-cart');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.submitTotalEl = this.submitButton.querySelector('[data-atc-total]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.error = false;

        this.variantPrices = this.parseVariantPrices();
        this.addonCents = 0;
        this.currentVariantPriceCents = null;
        this.setupTotalTracking();

        requestAnimationFrame(() => this.applySubmitButtonState());
      }

      /* ---------------- add-to-cart total (poster + add-ons) ---------------- */

      parseVariantPrices() {
        try {
          const el = this.querySelector('[data-atc-variant-prices]');
          return el ? JSON.parse(el.textContent) : {};
        } catch (e) {
          return {};
        }
      }

      setupTotalTracking() {
        if (!this.submitTotalEl) return;

        // Frame / passepartout picked in the inline configurator (event bubbles
        // up from inside this <product-form>).
        this.addEventListener('frame-config:change', (e) => {
          this.addonCents = Number(e.detail?.addonTotalCents) || 0;
          this.updateSubmitTotal();
        });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (String(event?.data?.sectionId) !== String(this.dataset.sectionId)) return;
            if (event.data.variant && event.data.variant.price != null) {
              this.currentVariantPriceCents = Number(event.data.variant.price);
            }
            this.updateSubmitTotal();
          });
          this.quantityUnsub = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.updateSubmitTotal());
        }

        const qtyInput = this.form.querySelector('[name="quantity"]');
        if (qtyInput) qtyInput.addEventListener('change', () => this.updateSubmitTotal());

        requestAnimationFrame(() => this.updateSubmitTotal());
      }

      currentBaseCents() {
        if (this.currentVariantPriceCents != null) return this.currentVariantPriceCents;
        const id = this.variantIdInput?.value;
        if (id && this.variantPrices && this.variantPrices[id] != null) return Number(this.variantPrices[id]);
        return null;
      }

      currentQuantity() {
        const q = parseInt(this.form.querySelector('[name="quantity"]')?.value || '1', 10);
        return Number.isFinite(q) && q > 0 ? q : 1;
      }

      updateSubmitTotal() {
        if (!this.submitTotalEl) return;
        const noVariant = !this.variantIdInput?.value || this.variantIdInput.disabled;
        if (this.isSizePlaceholderSelected() || noVariant) {
          this.submitTotalEl.hidden = true;
          this.submitTotalEl.textContent = '';
          return;
        }
        const base = this.currentBaseCents();
        if (base == null) {
          this.submitTotalEl.hidden = true;
          return;
        }
        const total = (base + (this.addonCents || 0)) * this.currentQuantity();
        this.submitTotalEl.textContent = ` · ${this.formatSubmitMoney(total)}`;
        this.submitTotalEl.hidden = false;
      }

      formatSubmitMoney(cents) {
        if (window.YarisMoney) return window.YarisMoney.format(cents);
        return (cents / 100).toFixed(2);
      }

      isSizePlaceholderSelected() {
        const productInfo = this.closest('product-info');
        if (!productInfo) return false;

        for (const sheet of productInfo.querySelectorAll('size-option-sheet')) {
          if (sheet.isPlaceholderSelected?.()) return true;
        }

        return false;
      }

      showSizeRequiredError() {
        this.handleErrorMessage(theme.variantStrings.selectSizeRequired);

        const sheet = this.closest('product-info')?.querySelector('size-option-sheet');
        const trigger = sheet?.querySelector('.size-option-sheet__trigger');

        trigger?.scrollIntoView({ behavior: 'smooth', block: 'center' });

        if (sheet?.classList.contains('size-option-sheet--mobile')) {
          sheet.open();
        } else {
          sheet?.querySelector('select')?.focus();
        }
      }

      applySubmitButtonState() {
        if (!this.submitButton || this.submitButton.classList.contains('loading')) return;

        if (this.isSizePlaceholderSelected()) {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.classList.add('button--size-required');
          if (this.submitButtonText) {
            this.submitButtonText.textContent = theme.variantStrings.addToCart;
          }
          return;
        }

        this.submitButton.classList.remove('button--size-required');

        const variantInput = this.variantIdInput;
        const isSoldOut = !variantInput?.value || variantInput.disabled;

        if (isSoldOut) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.removeAttribute('aria-disabled');
          if (this.submitButtonText) {
            this.submitButtonText.textContent = theme.variantStrings.soldOut;
          }
          return;
        }

        this.submitButton.removeAttribute('aria-disabled');
        this.submitButton.removeAttribute('disabled');
        if (this.submitButtonText) {
          this.submitButtonText.textContent = theme.variantStrings.addToCart;
        }
      }

      toggleLoadingState(loading) {
        if (!this.submitButton) return;
        if (loading) {
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.classList.add('loading');
        } else {
          this.submitButton.classList.remove('loading');
          if (!this.error) this.applySubmitButtonState();
        }
      }

      buildMainLineItemFromForm() {
        this.syncVariantIdFromPicker();
        const formData = new FormData(this.form);
        const item = {
          id: parseInt(formData.get('id'), 10),
          quantity: Math.max(1, parseInt(formData.get('quantity') || '1', 10)),
        };
        for (const [key, value] of formData.entries()) {
          const match = /^properties\[(.+)]$/.exec(key);
          if (match) {
            item.properties = item.properties || {};
            item.properties[match[1]] = value;
          }
        }
        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) item.selling_plan = parseInt(sellingPlan, 10);
        return item;
      }

      refreshCartSectionsFromRoot() {
        const sectionsToRender = this.cart?.getSectionsToRender() || [
          { id: 'mini-cart', section: 'mini-cart' },
          { id: 'cart-icon-bubble', section: 'cart-icon-bubble' },
          { id: 'mobile-cart-icon-bubble', section: 'mobile-cart-icon-bubble' },
        ];
        const q = sectionsToRender.map((s) => `sections=${encodeURIComponent(s.section)}`).join('&');
        return fetch(`/?${q}`)
          .then((r) => r.json())
          .then((sections) => {
            sectionsToRender.forEach((section) => {
              const el = document.getElementById(section.id);
              if (!el || !sections[section.section]) return;
              const html = new DOMParser()
                .parseFromString(sections[section.section], 'text/html')
                .querySelector('.shopify-section');
              if (html) el.innerHTML = html.innerHTML;
            });
            this.cart?.open?.();
            document.dispatchEvent(new CustomEvent('cart:updated'));
          });
      }

      addToCartWithFrameVariant(frameVariantId, onDone) {
        this.addToCartWithAddons([frameVariantId], onDone);
      }

      /**
       * Add the poster plus any number of add-on variants (frame, passepartout)
       * as separate cart line items in a single request.
       */
      addToCartWithAddons(addonVariantIds, onDone) {
        if (!this.cart) {
          window.location = theme.routes.cart_url;
          return;
        }

        this.handleErrorMessage();
        this.error = false;
        this.toggleLoadingState(true);

        const mainItem = this.buildMainLineItemFromForm();
        const cleanAddonIds = (addonVariantIds || []).filter((id) => id != null && id !== '');

        // Add-ons go in as plain line items (same qty, no extra properties)
        // so Shopify merges identical frame / passepartout variants.
        let addons = [];
        if (cleanAddonIds.length) {
          addons = cleanAddonIds.map((id) => ({
            id: parseInt(id, 10),
            quantity: mainItem.quantity || 1,
          }));
        }
        const items = [mainItem, ...addons];
        const sections = this.cart.getSectionsToRender().map((s) => s.id);

        const body = JSON.stringify({
          items,
          sections,
          sections_url: window.location.pathname,
        });

        fetch(`${theme.routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: String(mainItem.id),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.error = true;
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: String(mainItem.id),
              cartData: response,
            });

            if (response.sections) {
              this.cart.renderContents(response);
              onDone?.();
            } else {
              this.refreshCartSectionsFromRoot().finally(() => onDone?.());
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(theme.cartStrings?.error || 'Error');
            this.error = true;
          })
          .finally(() => {
            this.toggleLoadingState(false);
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          });
      }

      syncVariantIdFromPicker() {
        const productInfo = this.closest('product-info');
        if (!productInfo) return;

        for (const sheet of productInfo.querySelectorAll('size-option-sheet')) {
          if (typeof sheet.syncVariantIdToForm === 'function') {
            sheet.syncVariantIdToForm();
          }
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (this.isSizePlaceholderSelected()) {
          this.showSizeRequiredError();
          return;
        }

        this.syncVariantIdFromPicker();

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const skipFrameModal = this.dataset.ignoreFrameUpsellOnce === 'true';
        if (skipFrameModal) {
          delete this.dataset.ignoreFrameUpsellOnce;
        }

        // Inline configurator: if the customer already picked a frame/mat on the
        // page, add poster + add-ons directly and bypass the upsell modal.
        if (!skipFrameModal) {
          const configurator = document.querySelector(
            `product-frame-configurator[data-section-id="${this.dataset.sectionId}"]`
          );
          const addonIds =
            configurator && typeof configurator.getSelectedAddonVariantIds === 'function'
              ? configurator.getSelectedAddonVariantIds()
              : [];
          if (addonIds && addonIds.length) {
            this.addToCartWithAddons(addonIds);
            return;
          }
        }

        if (this.dataset.enableFrameUpsell === 'true' && !skipFrameModal) {
          const modal = document.querySelector(
            `frame-upsell-modal[data-section-id="${this.dataset.sectionId}"]`
          );
          if (modal) {
            modal.openForProductForm(this);
            return;
          }
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${theme.routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', 'true');
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = theme.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.applySubmitButtonState();
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.classList.remove('button--size-required');
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.removeAttribute('aria-disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.applySubmitButtonState();
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
